import { faAngleDown, faAngleUp } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { ReactElementLike } from "graphiql/dist/types";
import type { FC } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Link, Typography } from "@aviary";
import CAFlag from "@shared/assets/icons/flag-ca.svg";
import USFlag from "@shared/assets/icons/flag-usa.svg";
import { useInitialProps } from "@unauthenticated/catalog/context";
import { openCatalogRoutes } from "@unauthenticated/catalog/data/openCatalogRoutes";
import { l } from "@unauthenticated/catalog/locales/i18n";

import * as styles from "./CountrySelector.styles";

type Country = {
  country: string;
  fullName: string;
  label: string;
  flag: ReactElementLike;
  testId: string;
  route: string;
};

const CountrySelector: FC = () => {
  const { t } = useTranslation();
  const { fullscriptCountry } = useInitialProps();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const countries: Country[] = [
    {
      country: "US",
      fullName: t(l.common.UnitedStates),
      label: t(l.search.USALink),
      flag: <USFlag aria-labelledby={t(l.common.UnitedStates)} />,
      testId: "us-link",
      route: `${openCatalogRoutes.usApp}`,
    },
    {
      country: "CA",
      fullName: t(l.common.Canada),
      label: t(l.search.CanadaLink),
      flag: <CAFlag aria-labelledby={t(l.common.Canada)} />,
      testId: "us-link",
      route: `${openCatalogRoutes.caApp}`,
    },
  ];

  const getCountry = () => {
    const country = countries.filter(obj => {
      return obj.country === fullscriptCountry;
    });

    return country ? country[0] : null;
  };

  const toggleMenu = () => {
    isOpen ? setIsOpen(false) : setIsOpen(true);
  };

  const renderIcon = () => {
    if (isOpen) return <FontAwesomeIcon icon={faAngleDown} />;

    return <FontAwesomeIcon icon={faAngleUp} />;
  };

  const renderCountry = () => {
    return (
      <>
        <div css={styles.flag}>{getCountry().flag}</div>
        <div>
          <Typography css={styles.link} aria-label={getCountry().label}>
            {getCountry().fullName}
          </Typography>
        </div>
      </>
    );
  };

  const renderDropdown = () => {
    return (
      <ul css={styles.selectorContainer}>
        {countries.map((country, index) => {
          if (country.country !== fullscriptCountry) {
            return (
              <li key={index}>
                <div css={styles.flag}>{country.flag}</div>
                <Link
                  isUnderLined={false}
                  isColor="system"
                  noExternalIcon
                  href={`${country.route}${openCatalogRoutes.catalog}`}
                  aria-label={country.label}
                  data-testid="ca-link"
                >
                  {country.fullName}
                </Link>
              </li>
            );
          }
        })}
      </ul>
    );
  };

  return (
    <>
      <div css={styles.container} onClick={toggleMenu}>
        {renderCountry()}
        <div css={styles.icon}>{renderIcon()}</div>
      </div>
      {isOpen && renderDropdown()}
    </>
  );
};

export { CountrySelector };
